import { z } from 'zod';

// Create validation schema with translations
export const createAccountGeneralInfoSchema = (
  tForms: (key: string) => string
) =>
  z.object({
    companyType: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.companyTypeRequired')),
    language: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.languageRequired')),
    name: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.nameRequired'))
      .max(255, tForms('accountGeneralInfo.validation.nameMaxLength')),
    nameAddition: z
      .string()
      .max(255, tForms('accountGeneralInfo.validation.nameAdditionMaxLength'))
      .optional(),
    country: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.countryRequired')),
    street: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.streetRequired'))
      .max(255, tForms('accountGeneralInfo.validation.streetMaxLength')),
    streetNo: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.streetNoRequired'))
      .max(20, tForms('accountGeneralInfo.validation.streetNoMaxLength')),
    poBox: z
      .string()
      .max(20, tForms('accountGeneralInfo.validation.poBoxMaxLength'))
      .optional(),
    postalCode: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.postalCodeRequired'))
      .max(10, tForms('accountGeneralInfo.validation.postalCodeMaxLength')),
    city: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.cityRequired'))
      .max(100, tForms('accountGeneralInfo.validation.cityMaxLength')),
    canton: z
      .string()
      .max(50, tForms('accountGeneralInfo.validation.cantonMaxLength'))
      .optional(),
    website: z
      .string()
      .url(tForms('accountGeneralInfo.validation.websiteInvalid'))
      .optional()
      .or(z.literal(''))
      .transform(val => (val === '' ? undefined : val)),
    industry: z
      .string()
      .min(1, tForms('accountGeneralInfo.validation.industryRequired')),
    commercialRegisterNumber: z
      .string()
      .min(
        1,
        tForms('accountGeneralInfo.validation.commercialRegisterNumberRequired')
      )
      .max(
        50,
        tForms(
          'accountGeneralInfo.validation.commercialRegisterNumberMaxLength'
        )
      ),
  });

// Create bank connection validation schema
export const createBankConnectionSchema = (tForms: (key: string) => string) =>
  z.object({
    bankConnections: z
      .array(
        z.object({
          bankName: z
            .string()
            .min(1, tForms('accountBanking.validation.bankNameRequired'))
            .max(100, tForms('accountBanking.validation.bankNameMaxLength')),
          accountNumber: z
            .string()
            .min(1, tForms('accountBanking.validation.accountNumberRequired'))
            .max(
              50,
              tForms('accountBanking.validation.accountNumberMaxLength')
            ),
          iban: z
            .string()
            .min(1, tForms('accountBanking.validation.ibanRequired'))
            .regex(
              /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
              tForms('accountBanking.validation.ibanInvalid')
            ),
          bic: z
            .string()
            .min(1, tForms('accountBanking.validation.bicRequired'))
            .regex(
              /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
              tForms('accountBanking.validation.bicInvalid')
            ),
          currency: z
            .string()
            .min(1, tForms('accountBanking.validation.currencyRequired')),
        })
      )
      .min(1, tForms('accountBanking.validation.atLeastOneBankRequired')),
  });

// Legacy schema for backward compatibility (will be deprecated) // Legacy schema for backward compatibility (will be deprecated)
export const accountGeneralInfoSchema = z.object({
  companyType: z.string().min(1, 'Company type is required'),
  language: z.string().min(1, 'Language is required'),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must be less than 255 characters'),
  nameAddition: z
    .string()
    .max(255, 'Name addition must be less than 255 characters')
    .optional(),
  country: z.string().min(1, 'Country is required'),
  street: z
    .string()
    .min(1, 'Street is required')
    .max(255, 'Street must be less than 255 characters'),
  streetNo: z
    .string()
    .min(1, 'Street number is required')
    .max(20, 'Street number must be less than 20 characters'),
  poBox: z
    .string()
    .max(20, 'P.O. Box must be less than 20 characters')
    .optional(),
  postalCode: z
    .string()
    .min(1, 'Postal code is required')
    .max(10, 'Postal code must be less than 10 characters'),
  city: z
    .string()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters'),
  canton: z
    .string()
    .max(50, 'Canton must be less than 50 characters')
    .optional(),
  website: z
    .string()
    .url('Please enter a valid URL')
    .optional()
    .or(z.literal(''))
    .transform(val => (val === '' ? undefined : val)),
  industry: z.string().min(1, 'Industry is required'),
  commercialRegisterNumber: z
    .string()
    .min(1, 'Commercial register number is required')
    .max(50, 'Commercial register number must be less than 50 characters'),
});

// Token validation schema
export const tokenValidationSchema = z.object({
  token: z.string().min(1, 'Token is required'),
});

// Custom validators for specific fields
export const validatePostalCode = (
  country: string,
  postalCode: string
): boolean => {
  const patterns: Record<string, RegExp> = {
    CH: /^\d{4}$/, // Swiss postal codes are 4 digits
    DE: /^\d{5}$/, // German postal codes are 5 digits
    AT: /^\d{4}$/, // Austrian postal codes are 4 digits
    FR: /^\d{5}$/, // French postal codes are 5 digits
  };

  const pattern = patterns[country];
  return pattern ? pattern.test(postalCode) : true; // Default to valid if pattern not found
};

export const validateCommercialRegisterNumber = (
  country: string,
  number: string
): boolean => {
  const patterns: Record<string, RegExp> = {
    CH: /^CHE-\d{3}\.\d{3}\.\d{3}$/, // Swiss UID format
    DE: /^HRB \d+$/, // German format example
    AT: /^FN \d+[a-z]?$/, // Austrian format example
  };

  const pattern = patterns[country];
  return pattern ? pattern.test(number) : true; // Default to valid if pattern not found
};
